import React, { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PasswordStrengthMeter, { scorePassword } from '../components/PasswordStrengthMeter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const { score } = scorePassword(password);
  const canSubmit = token && password && confirm && password === confirm && score >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/users/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-[#1F3A68] mb-2">Set a new password</h1>

          {!token && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              This link is missing a reset token. <Link to="/forgot-password" className="font-semibold underline">Request a new link</Link>.
            </div>
          )}

          {token && done && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
              Password updated. Redirecting you to sign in…
            </div>
          )}

          {token && !done && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="pw" className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                <input
                  id="pw"
                  type="password"
                  required
                  minLength={10}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                />
                <PasswordStrengthMeter password={password} />
              </div>
              <div>
                <label htmlFor="cf" className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
                <input
                  id="cf"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full h-12 border border-slate-300 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-[#1F3A68]/30"
                />
                {confirm && password !== confirm && (
                  <p className="text-xs text-red-600 mt-1">Passwords do not match.</p>
                )}
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full h-12 bg-[#1F3A68] text-white rounded-lg font-semibold hover:bg-[#1F3A68]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Updating…' : 'Reset password'}
              </button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
